"use client";
import React, { useState, lazy, Suspense } from "react";
import Image from "next/image";
import Link from "next/link";
import { ShoppingBag, Heart, User, Menu, X } from "lucide-react";
import SignUpPopup from "./signup/EmailCollectionPopup";

const SearchBar = lazy(() => import("./search"));
const Navbar = lazy(() => import("./navbar"));

const Header = ({ sessionId, onEmailSubmit }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
 
  const [isSignUpPopupVisible, setIsSignUpPopupVisible] = useState(false);

  const announcements = [
    "🎁 Secured Packaging",
    "🚚 Free Shipping on ₹999+",
    "💝 Handcrafted Gifts",
    "✨ Celebrate Life's Special Moments",
  ];

  // Close mobile menu when a link is clicked
  const handleMobileNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="w-full" role="banner">
      {/* Sign-Up Popup */}
      {isSignUpPopupVisible && (
        <SignUpPopup
          onClose={() => setIsSignUpPopupVisible(false)}
          onEmailSubmit={onEmailSubmit} // Pass the callback to SignUpPopup
        />
      )}

      {/* Announcement Bar */}
      <div className="bg-gradient-to-r from-red-800 via-red-600 to-red-800 text-white">
        <div className="h-8 overflow-hidden">
          <div className="animate-marquee whitespace-nowrap flex items-center h-full">
            {[...announcements, ...announcements].map((text, i) => (
              <span key={i} className="mx-4 text-sm font-medium">
                {text}
                {i < announcements.length * 2 - 1 && (
                  <span className="mx-3 opacity-50">•</span>
                )}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Mobile Controls */}
            <div className="flex md:hidden gap-4">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-gray-700 hover:text-red-600 transition-colors"
                aria-label="Toggle menu"
                aria-expanded={isMobileMenuOpen}
              >
                <Menu className="h-6 w-6" />
              </button>
             
            </div>

            {/* Logo */}
            <div className="absolute left-1/2 -translate-x-1/2 md:relative md:left-0 md:transform-none">
              <Link href="/" className="block">
                <Image
                  src="/logo-R.png"
                  alt="Harsh Hasthkala"
                  width={150}
                  height={60}
                  className="h-14 md:h-14 w-auto"
                  priority
                />
              </Link>
            </div>

            {/* Desktop Search */}
            <div className="hidden md:block flex-1 max-w-lg mx-8">
              <Suspense
                fallback={
                  <div className="h-12 bg-gray-100 rounded-full animate-pulse" />
                }
              >
                <SearchBar />
              </Suspense>
            </div>

            {/* Action Icons */}
            <nav className="flex items-center gap-4 md:gap-6">
              <Link
                href="/like"
                className="text-gray-700 hover:text-red-600 transition-colors"
                aria-label="Wishlist"
              >
                <Heart className="h-6 w-6" />
              </Link>
              <Link
                href="/product-cart"
                className="text-gray-700 hover:text-red-600 transition-colors"
                aria-label="Shopping cart"
              >
                <ShoppingBag className="h-6 w-6" />
              </Link>
              <Link
                href="/account"
                className="text-gray-700 hover:text-red-600 transition-colors"
                aria-label="Account"
              >
                <User className="h-6 w-6" />
              </Link>
            </nav>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div
        className={`fixed inset-0 bg-black/50 z-50 transition-opacity md:hidden ${
          isMobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        <div
          className={`absolute top-0 left-0 w-4/5 max-w-sm h-full bg-white transform transition-transform ${
            isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <Suspense
            fallback={<div className="h-full bg-gray-50 animate-pulse" />}
          >
            <Navbar isMobile={true} onClose={handleMobileNavClick} />
          </Suspense>
        </div>
      </div>

      {/* Mobile Search Bar */}
      <div className="bg-white shadow-sm px-4 py-2 md:hidden">
        <Suspense
          fallback={
            <div className="h-12 bg-gray-100 rounded-full animate-pulse" />
          }
        >
          <SearchBar isMobile={true} />
        </Suspense>
      </div>

      {/* Desktop Navigation */}
      <div className="hidden md:block border-b border-gray-200">
        <Suspense fallback={<div className="h-14 bg-gray-50 animate-pulse" />}>
          <Navbar />
        </Suspense>
      </div>

      {/* Mobile Search Overlay */}
      {isSearchOpen && (
        <div className="fixed inset-0 z-50 bg-black/50">
          <div className="bg-white p-4 animate-slideDown">
            <div className="relative">
              <Suspense
                fallback={
                  <div className="h-12 bg-gray-100 rounded-full animate-pulse" />
                }
              >
                <SearchBar isMobile={true} />
              </Suspense>
              <button
                onClick={() => setIsSearchOpen(false)}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                aria-label="Close search"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx global>{`
        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        @keyframes slideDown {
          0% {
            transform: translateY(-100%);
          }
          100% {
            transform: translateY(0);
          }
        }
        @keyframes fadeIn {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </header>
  );
};

export default Header;
